import React, { useState } from 'react'
import {
  AlertTriangle,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Sparkles,
  UserRound,
  XCircle,
} from 'lucide-react'

interface EvidenceItem {
  criterion: string
  evidence: string
}

interface Category {
  key: string
  label: string
  criteria: Array<{ criterion: string; importance: 'mandatory' | 'important' | 'optional' }>
  raw_score: number
  weight: number
  weight_explanation: string
  weighted_contribution: number
  criteria_count: number
  matched: EvidenceItem[]
  partial_matches: EvidenceItem[]
  missing: EvidenceItem[]
  unverifiable: EvidenceItem[]
  explanation: string
  confidence: 'high' | 'medium' | 'low'
}

export interface CandidateOfferComparison {
  schema_version: number
  score_source: 'ai_simulation_backend_verified'
  overall_score: number
  categories: Category[]
  strongest_categories: string[]
  weakest_categories: string[]
  main_gaps: string[]
  hiring_risks: string[]
  interview_checks: string[]
  recommendation: {
    decision: 'proceed' | 'consider' | 'reject'
    explanation: string
  }
  alternative_roles: string[]
}

interface Identity {
  id: number
  name?: string
  role?: string
  job_title?: string
}

interface Props {
  comparison: CandidateOfferComparison
  candidate?: Identity | null
  offer?: Identity | null
  language?: string
}

const score = (value: number) => `${Number(value).toFixed(2)} %`

const Evidence = ({ title, items, tone }: { title: string; items: EvidenceItem[]; tone: 'match' | 'partial' | 'missing' | 'unknown' }) => {
  const styles = {
    match: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100',
    partial: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100',
    missing: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-100',
    unknown: 'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200',
  }[tone]
  const Icon = tone === 'match' ? CheckCircle2 : tone === 'partial' ? CircleHelp : tone === 'missing' ? XCircle : CircleHelp
  return (
    <div className={`rounded-lg border p-3 ${styles}`}>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-bold"><Icon className="h-3.5 w-3.5" />{title}</p>
      {items.length ? (
        <ul className="space-y-2 text-xs">{items.map((item, index) => (
          <li key={`${item.criterion}-${index}`}>
            <span className="font-semibold">{item.criterion}</span>
            <span className="block opacity-80">{item.evidence}</span>
          </li>
        ))}</ul>
      ) : <span className="text-xs opacity-60">—</span>}
    </div>
  )
}

const List = ({ title, items }: { title: string; items: string[] }) => {
  if (!items.length) return null
  return (
    <div className="rounded-lg border border-border bg-background/80 p-3">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</p>
      <ul className="space-y-1.5 text-sm">{items.map((item, index) => <li key={`${item}-${index}`}>• {item}</li>)}</ul>
    </div>
  )
}

export function CandidateOfferMatchingCard({ comparison, candidate, offer, language = 'fr' }: Props) {
  const fr = language.startsWith('fr')
  const [expanded, setExpanded] = useState<string | null>(comparison.categories[0]?.key || null)
  const decision = {
    proceed: fr ? 'Poursuivre' : 'Proceed',
    consider: fr ? 'À considérer' : 'Consider',
    reject: fr ? 'Ne pas poursuivre' : 'Reject',
  }[comparison.recommendation.decision]
  const decisionStyle = comparison.recommendation.decision === 'proceed'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100'
    : comparison.recommendation.decision === 'consider'
      ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100'
      : 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-100'

  return (
    <div className="space-y-4 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-cyan-50/50 p-4 shadow-sm dark:to-cyan-950/10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-bold"><UserRound className="h-4 w-4 text-primary" />{candidate?.name || (fr ? 'Candidat sélectionné' : 'Selected candidate')}</div>
          {candidate?.role && <p className="pl-6 text-xs text-muted-foreground">{candidate.role}</p>}
          <div className="flex items-center gap-2 text-sm font-semibold"><Briefcase className="h-4 w-4 text-indigo-600" />{offer?.job_title || (fr ? 'Offre sélectionnée' : 'Selected offer')}</div>
          <p className="pt-1 text-[11px] text-muted-foreground">{fr ? 'Simulation analytique générée par l’IA' : 'AI-generated analytical simulation'}</p>
        </div>
        <div className="rounded-xl border border-primary/20 bg-background px-4 py-3 text-right shadow-sm">
          <p className="text-xs font-semibold text-muted-foreground">{fr ? 'Compatibilité globale' : 'Overall compatibility'}</p>
          <p className="text-3xl font-black text-primary">{score(comparison.overall_score)}</p>
        </div>
      </div>

      <div className="space-y-2">
        {comparison.categories.map((category) => {
          const open = expanded === category.key
          return (
            <div key={category.key} className="overflow-hidden rounded-xl border border-border bg-background/85">
              <button type="button" onClick={() => setExpanded(open ? null : category.key)} className="grid w-full items-center gap-2 p-3 text-left sm:grid-cols-[minmax(170px,1.4fr)_repeat(4,minmax(80px,1fr))_auto]">
                <span className="font-bold text-foreground">{category.label}</span>
                <span><span className="block text-[10px] uppercase text-muted-foreground">{fr ? 'Score brut' : 'Raw score'}</span><strong>{score(category.raw_score)}</strong></span>
                <span><span className="block text-[10px] uppercase text-muted-foreground">{fr ? 'Poids' : 'Weight'}</span><strong>{score(category.weight)}</strong></span>
                <span><span className="block text-[10px] uppercase text-muted-foreground">{fr ? 'Score pondéré' : 'Weighted score'}</span><strong>{category.weighted_contribution.toFixed(2)} pts</strong></span>
                <span><span className="block text-[10px] uppercase text-muted-foreground">{fr ? 'Critères' : 'Criteria'}</span><strong>{category.criteria_count}</strong></span>
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {open && (
                <div className="space-y-3 border-t border-border p-3">
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                    <Evidence title={fr ? 'Correspondances' : 'Matched'} items={category.matched} tone="match" />
                    <Evidence title={fr ? 'Partielles' : 'Partial'} items={category.partial_matches} tone="partial" />
                    <Evidence title={fr ? 'Manquants confirmés' : 'Confirmed gaps'} items={category.missing} tone="missing" />
                    <Evidence title={fr ? 'Non vérifiables' : 'Unverifiable'} items={category.unverifiable} tone="unknown" />
                  </div>
                  <div className="rounded-lg bg-primary/5 p-3 text-sm">
                    <div className="flex gap-2"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><p>{category.explanation}</p></div>
                    <div className="mt-2 flex flex-wrap gap-2 pl-6 text-xs text-muted-foreground">
                      <span>{fr ? 'Confiance' : 'Confidence'}: {category.confidence}</span>
                      <span>·</span>
                      <span>{category.weight_explanation}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <List title={fr ? 'Catégories les plus fortes' : 'Strongest categories'} items={comparison.strongest_categories} />
        <List title={fr ? 'Catégories les plus faibles' : 'Weakest categories'} items={comparison.weakest_categories} />
        <List title={fr ? 'Principaux écarts' : 'Main gaps'} items={comparison.main_gaps} />
        <List title={fr ? 'Risques de recrutement' : 'Hiring risks'} items={comparison.hiring_risks} />
        <List title={fr ? 'À vérifier en entretien' : 'Interview checks'} items={comparison.interview_checks} />
        <List title={fr ? 'Rôles alternatifs' : 'Alternative roles'} items={comparison.alternative_roles} />
      </div>

      <div className={`rounded-xl border p-4 ${decisionStyle}`}>
        <p className="mb-1 flex items-center gap-2 text-sm font-bold"><AlertTriangle className="h-4 w-4" />{fr ? 'Recommandation' : 'Recommendation'} · {decision}</p>
        <p className="text-sm">{comparison.recommendation.explanation}</p>
      </div>
    </div>
  )
}
